
import React, { useState, useEffect } from 'react';
import WhatsappCard from '../components/WhatsappCard';
import { Quantum } from 'ldrs/react';
import 'ldrs/react/Quantum.css';
import { NotificationManager } from 'react-notifications';
import DashboardFooter from '../components/DashboardFooter';

const WhatsappAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAppointments, setSelectedAppointments] = useState([]);
  const [latestOnly, setLatestOnly] = useState(false);
  const [serviceByTime, setServiceByTime] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [updateFields, setUpdateFields] = useState({
    address: '',
    notes: '',
    service: '',
    service_required_time: '',
    status: '',
  });

  const [highlightDate, setHighlightDate] = useState(() => {
  const today = new Date();
  return today.toISOString().split('T')[0];
});
  // Fetch Appointments
  const fetchWhatsappAppointments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_HOST_API_URL}/admin-user/whatsapp-appointments/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) throw new Error(`HTTP error! ${response.status}`);
      const result = await response.json();
      setAppointments(result?.data || []);
    } catch (err) {
      console.error("Error fetching WhatsApp appointments:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWhatsappAppointments();
  }, []);

  // Update Appointment
  const updateAppointment = async (appointmentId, updatedFields) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`${process.env.REACT_APP_HOST_API_URL}/admin-user/whatsapp-appointments/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ appointment_id: appointmentId, ...updatedFields }),
      });

      if (!response.ok) throw new Error(`HTTP error! ${response.status}`);
      await response.json();
      NotificationManager.success("Appointment updated successfully");
      fetchWhatsappAppointments();
    } catch (err) {
      NotificationManager.error('Error updating appointment: ' + err.message);
    }
  };

  // Filtered Appointments
  const filteredAppointments = appointments.filter(a => {
    const matchesSearch =
      a.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' || a.status === statusFilter;

    const matchesLatest = latestOnly
      ? (() => {
          if (!a.service_required_time) return false;

          const today = new Date();
          const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const twoDaysBefore = new Date(todayStart);
          twoDaysBefore.setDate(todayStart.getDate() - 2);

          const serviceDate = new Date(a.service_required_time);
          return serviceDate >= twoDaysBefore;
        })()
      : true;

    return matchesSearch && matchesStatus && matchesLatest;
  });

  // Group by date helper
  const groupByDate = (key) => {
    return filteredAppointments.reduce((acc, curr) => {
      let date = curr[key] ? curr[key].split('T')[0] : 'Unknown';
      if (!acc[date]) acc[date] = [];
      acc[date].push(curr);
      return acc;
    }, {});
  };

  // Group by either service_required_time or created_at
  const groupedAppointments = groupByDate(
    latestOnly || serviceByTime ? 'service_required_time' : 'created_at'
  );

  // Sorted dates
  const dates = Object.keys(groupedAppointments).sort((a, b) => {
    const da = new Date(a), db = new Date(b);
    if (isNaN(da.getTime())) return 1;
    if (isNaN(db.getTime())) return -1;
    return da - db;
  });

  // Checkbox Handlers
  const handleCheckboxToggle = (appointmentId) => {
    setSelectedAppointments(prev =>
      prev.includes(appointmentId)
        ? prev.filter(id => id !== appointmentId)
        : [...prev, appointmentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAppointments.length === filteredAppointments.length) {
      setSelectedAppointments([]);
    } else {
      const filteredIds = filteredAppointments.map(a => a.id);
      setSelectedAppointments(filteredIds);
    }
  };

  // Format Datetime
  const formatForBackend = (datetimeLocal) => {
    if (!datetimeLocal) return null;
    const [date, time] = datetimeLocal.split('T');
    if (!date || !time) return null;
    return `${date} ${time}:00`;
  };

  const formatDatetimeForInput = (datetime) => datetime ? datetime.slice(0, 16) : '';

  const openUpdateModal = (appointment) => {
    setCurrentAppointment(appointment);
    setUpdateFields({
      address: appointment.address || '',
      notes: appointment.notes || '',
      service: appointment.service || '',
      service_required_time: formatDatetimeForInput(appointment.service_required_time),
      status: appointment.status || '',
    });
    setShowUpdateModal(true);
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdateFields(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateSubmit = async () => {
    if (!currentAppointment) return;
    const updatedData = {
      address: updateFields.address,
      notes: updateFields.notes,
      service: updateFields.service,
      status: updateFields.status,
    };
    if (updateFields.service_required_time) {
      updatedData.service_required_time = formatForBackend(updateFields.service_required_time);
    }
    await updateAppointment(currentAppointment.id, updatedData);
    setShowUpdateModal(false);
    setCurrentAppointment(null);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 'calc(100vh - 40px)', width: '100vw', marginLeft: '-280px' }}>
        <Quantum size="45" speed="1.75" color="blue" />
      </div>
    );
  }

  return (
     <div className="d-flex flex-column min-vh-100 pb-2 px-3">
    {/* Main content area (scrolls normally) */}
    <div className="flex-grow-1">
    <div className="container-fluid px-2 px-md-4 " style={{ paddingTop: '20px' }}>
      {/* Header */}
      <div className="row mb-4 bg-white shadow-sm border-bottom sticky-top py-3 px-md-3 px-2 rounded"
        style={{ background: "linear-gradient(135deg, #25D366, #128C7E)" }}
      >
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center" style={{ gap: '1rem' }}>
          <div>
            <h3 className="text-light mb-1">WhatsApp Appointments</h3>
            <p className="text-light mb-0">View and manage WhatsApp booking requests</p>
          </div>
          <div className="text-md-end">
            <h5 className="text-light mb-1">Total: {filteredAppointments.length}</h5>
            <p className="text-light mb-0">Results filtered by search or date</p>
          </div>
        </div>

        {/* Filters */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mt-3"
          style={{ gap: '1rem' }}
        >
          <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.75rem' }}>
            <input
              style={{
                minWidth: '260px',
                border: 'none',
                outline: 'none',
                padding: '6px 10px',
                borderRadius: '6px',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
              type="search"
              placeholder="Search by name or mobile"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="form-check d-flex align-items-center">
              <input
                className="form-check-input me-2"
                type="checkbox"
                id="serviceByTimeCheckbox"
                checked={serviceByTime}
                onChange={() => setServiceByTime(!serviceByTime)}
              />
              <label className="form-check-label text-light" htmlFor="serviceByTimeCheckbox">
                Service by required time
              </label>
            </div>

            <div className="form-check d-flex align-items-center">
              <input
                className="form-check-input me-2"
                type="checkbox"
                id="latestOnlyCheckbox"
                checked={latestOnly}
                onChange={() => setLatestOnly(!latestOnly)}
              />
              <label className="form-check-label text-light" htmlFor="latestOnlyCheckbox">
                Latests
              </label>
            </div>
          </div>

          {/* Status Filter */}
          <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
            <select
              id="statusFilter"
              className="form-select form-select-sm"
              style={{ minWidth: '160px' }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="pending_confirmation">Pending Confirmation</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Appointments Table */}
      <div className="appointment-container px-3 mt-4">
        <div className="table-responsive">
          <table className="table align-middle">
            <thead className="table-light">
              <tr>
                {dates.map((date) => (
                  <th key={date} scope="col" className="text-center text-muted pt-2">
                    <span className={`text-center p-2 rounded ${
                      date === highlightDate ? 'bg-success text-light' : 'text-muted'
                    }`}>
                      {date}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {dates.map((date) => (
                  <td key={date} style={{ minWidth: '12rem', verticalAlign: 'top' }}>
                    {groupedAppointments[date]?.map((a) => (
                      <WhatsappCard key={a.id} appointment={a}
                        isSelected={selectedAppointments.includes(a.id)}
                        onCheckboxChange={() => handleCheckboxToggle(a.id)}
                        onEdit={openUpdateModal} />
                    ))}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Update Modal */}
      {showUpdateModal && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog" role="document">
            <div className="modal-content" >
              <div className="modal-header" style={{ background: "linear-gradient(135deg, #25D366, #128C7E)" }}>
                <h5 className="text-light">Update Appointment</h5>
                <button type="button" className="btn-close" onClick={() => setShowUpdateModal(false)}></button>
              </div>
              <div className="modal-body">
                {Object.entries(updateFields).map(([key, value]) => (
                  <div className="mb-2" key={key}>
                    <label className="form-label">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
                    {key === 'notes' ? (
                      <textarea className="form-control" name={key} value={value} onChange={handleUpdateChange}></textarea>
                    ) : key === 'status' ? (
                      <select className="form-select" name={key} value={value} onChange={handleUpdateChange}>
                        <option value="pending_confirmation">Pending Confirmation</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Cancelled</option>
                      </select>
                    ) : key === 'service_required_time' ? (
                      <input type="datetime-local" className="form-control" name={key} value={value} onChange={handleUpdateChange} />
                    ) : (
                      <input type="text" className="form-control" name={key} value={value} onChange={handleUpdateChange} />
                    )}
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowUpdateModal(false)}>Cancel</button>
                <button className="btn btn-outline-success" onClick={handleUpdateSubmit}>Update</button>
              </div>
            </div>
          </div>
        </div>
      )}
</div>
</div>
      <DashboardFooter />
    </div>
  );
};

export default WhatsappAppointments;
